import moment, {
  Moment,
  MomentFormatSpecification
} from 'moment';
import momentTimezone from 'moment-timezone';
import {
  isArray,
  isEmpty,
  isNil,
  isString
} from 'lodash';
import messagesDE from '../translations/de';
import messagesEN from '../translations/en';
import { DATE_FORMAT } from '../constants/date.constants';
import { ELSDropDownOption } from '../components/els.components';
import { Application } from '../apis/eols-app-link/eols-app-link.constants';
import {
  CourseSectionDto,
  EvolveProductTypeKey
} from '../apis/eols-course-crud/eols-course-crud.dtos';
import {
  Entitlement,
  RecordAny,
  RecordObject,
  RecordString
} from '../models/common.models';
import {
  AppLinkCookies,
  AppLinkData
} from '../apis/eols-app-link/eols-app-link.dtos';

export const formatDate = (str: string): string => moment(str).format(DATE_FORMAT);

export const parseDate = (str: string): Date => {
  const valid = moment(str, DATE_FORMAT, true).isValid();
  return valid ? moment(str, DATE_FORMAT, true).toDate() : undefined;
};

export const getLocalMomentInsFromServicesUTC = (date: string, format?: MomentFormatSpecification): Moment => {
  return moment.utc(date, format).local();
};

export const getLocalDateFromServicesUTC = (date: string): Date => {
  return getLocalMomentInsFromServicesUTC(date).toDate();
};
export type windowModeType =
  { mobile: boolean; tablet: boolean; desktop: boolean; wide: boolean }
export const getWindowMode = (): windowModeType  => {
  const { innerWidth } = window;
  const mode = { mobile: false, tablet: false, desktop: false, wide: false };
  if (innerWidth < 600) {
    return { ...mode, mobile: true };
  }
  if (innerWidth >= 600 && innerWidth < 900) {
    return { ...mode, tablet: true };
  }
  if (innerWidth >= 900 && innerWidth < 1200) {
    return { ...mode, desktop: true };
  }
  if (innerWidth >= 1200) {
    return { ...mode, wide: true };
  }
  return mode;
};

export const getTopPosition = (): number => window.pageYOffset || document.documentElement.scrollTop;

export const getCleanSearchParamsFromObject = (searchParams: RecordObject | Record<string, RecordObject>): URLSearchParams => {
  const _searchParams = new URLSearchParams();
  Object.keys(searchParams).forEach(key => {
    const val = searchParams[key];
    if (isNil(val)) {
      return;
    }
    if (isArray(val) && !val.length) {
      return;
    }
    if (isString(val)) {
      _searchParams.append(key.toString(), val);
    } else if (isArray(val)) {
      val.forEach((valItem, index) => {
        if (!isNil(valItem)) {
          // Handle array of objects with indexed notation
          if (typeof valItem === 'object' && valItem !== null && !isArray(valItem)) {
            Object.keys(valItem).forEach(objKey => {
              if (!isNil(valItem[objKey])) {
                _searchParams.append(`${key}[${index}].${objKey}`, valItem[objKey].toString());
              }
            });
          } else {
            // Handle array of primitives (existing behavior)
            _searchParams.append(key.toString(), valItem.toString());
          }
        }
      });
    } else {
      _searchParams.append(key.toString(), JSON.stringify(val));
    }
  });
  return _searchParams;
};

export const addSearchParams = (url: string, searchParams: RecordObject): string => {

  if (!searchParams) {
    return url;
  }

  const [baseUrl, existingParams = ''] = url.split('?');

  const urlSearchParams = getCleanSearchParamsFromObject(searchParams);

  const combinedParams = existingParams
    ? `${existingParams}&${urlSearchParams.toString()}`
    : urlSearchParams.toString();

  if (!combinedParams) {
    return url;
  }
  return `${baseUrl}?${combinedParams}`;
};

export const deleteSearchParams = (url: string, existingSearchParams: RecordObject, deletingParams: string[]): string => {
  const urlSearchParams = getCleanSearchParamsFromObject(existingSearchParams as RecordObject);
  deletingParams.forEach(param => urlSearchParams.has(param) && urlSearchParams.delete(param));
  if (isEmpty(urlSearchParams.toString())) {
    return url;
  }
  return `${url}?${urlSearchParams.toString()}`;
};

export const getObjectFromSearchParams = (search: string): RecordAny => {
  if (!search) {
    return {};
  }
  const searchParams = new URLSearchParams(search);
  return Object.fromEntries(searchParams.entries());
};

// Carry the ?adminOverride=true param forward onto an internal navigation target.
// The admin override is gated entirely on this URL param, so internal navigation
// (new chat, switching chats, returning to chat) must preserve it or the admin
// override turns off unintentionally. Only re-applies the param when it is
// currently 'true'; absence or 'false' is left off so the override still disables
// when the param is removed/flipped.
export const preserveAdminOverrideParam = (targetPath: string, search: string): string => {
  const isAdminOverride = getObjectFromSearchParams(search).adminOverride === 'true';
  return isAdminOverride ? addSearchParams(targetPath, { adminOverride: true }) : targetPath;
};

export const isValidDate = (str: string | Date, format: MomentFormatSpecification = DATE_FORMAT, strict = true): boolean => {
  return moment(str, format, strict).isValid();
};

/**
 * There are 2 fields in DateTimeComponent: DateComponent and TimeComponent
 * originalDateInput is only bubbled up when we change value in DateComponent
 * originalDateInput is string while value is the unstrictly converted Date so have to validate originalDateInput to ensure correct format
 *
 */
export const validateDateTimeChange = (value: Date, originalDateInput: string): boolean => {
  if (originalDateInput === undefined) {
    return isValidDate(value);
  }
  return isValidDate(originalDateInput);
};

export const messages: Record<string, RecordString> = {
  de: messagesDE,
  en: messagesEN
};

export const getLanguage = (): string => {
  const defaultLanguage = 'en';
  const [firstLanguage] = navigator.languages || [];
  let browserLanguage = firstLanguage || navigator.language;
  const supporting = ['en', 'de'];
  if (supporting.includes(browserLanguage)) {
    return browserLanguage;
  }
  browserLanguage = browserLanguage.substring(0, 2);

  if (supporting.includes(browserLanguage)) {
    return browserLanguage;
  }

  return defaultLanguage;
};

export const SELECT_OPTION: ELSDropDownOption = {
  name: '--Select--',
  value: ''
};

export const getApplicationContext = (): Application => {
  return Application.NHE_AI;
};


const regexAnyDigit = '\\d';
export const getClientZoneAbbr = (): string => {
  const clientZone = momentTimezone.tz.guess();
  const currentZoneAbbr = momentTimezone.tz(momentTimezone(), clientZone).zoneAbbr();
  const doesContainDigit =new RegExp(regexAnyDigit).test(currentZoneAbbr);
  if (doesContainDigit) {
    return clientZone;
  }
  return currentZoneAbbr;
};

export const clientTimeZone = momentTimezone.tz.guess();

export const clientZoneAbbr = getClientZoneAbbr();

export const convertIsbnsToNormalizedString = (isbns: string[]): string => {
  if (!isbns || !isbns.length) {
    return '';
  }
  return isbns.sort().join(',');
};
export const getEntitlements = (courseSection: CourseSectionDto): Entitlement[] => {
  return courseSection.entitlements.map((entitlement) => {

    if (!entitlement || !entitlement.data) {
      return {
        entitlement,
        evolveProduct: null
      };
    }

    return {
      entitlement,
      evolveProduct: JSON.parse(entitlement.data)
    };
  }).filter((item) => {
    return item.evolveProduct;
  });
};
export const getIsbnsForVantageCourse = (tokenIsbns: string, course: CourseSectionDto): string[] => {
  if (!course || !course.entitlements || !course.entitlements.length || !tokenIsbns) {
    return null;
  }

  const tokenIsbnsArr = tokenIsbns.split(',');

  if (!tokenIsbnsArr) {
    return null;
  }

  const entitlements = getEntitlements(course);

  if (!entitlements) {
    return null;
  }

  return tokenIsbnsArr.reduce((acc, isbn) => {

    const entitlement = entitlements.find((_entitlement) => {
      return _entitlement.entitlement.isbn === isbn;
    });

    if (!entitlement) {
      return acc;
    }

    if (entitlement.evolveProduct.productTypeKey !== EvolveProductTypeKey.SHERPATH_VANTAGE_IA) {
      return [
        ...acc,
        isbn
      ];
    }
    return entitlement.evolveProduct.realVantageComponent.reduce((_acc, component) => {
      if (component.productTypeKey.includes('_nss')) {
        return _acc;
      }
      return [
        ..._acc,
        component.isbn
      ];
    }, acc);
  }, []);

};

export const getParentLinkId = (appLinkData: AppLinkData, appLinkCookies: AppLinkCookies): string => {
  if (appLinkData?.parentLinkId) {
    return appLinkData.parentLinkId;
  }
  return appLinkCookies ? appLinkCookies.linkId : null;
};
